<template>
	<div
		class="g-block-image-feature-card"
		:class="{
			'g-block-image-feature-card--edit': editable,
			'is-active': editable && active,
			'g-block-image-feature-card--h5': h5,
			'is-clickable': isClickable,
		}"
		:style="cardStyle"
		@click="onWrapClick"
	>
		<div class="g-block-image-feature-card__cover" :style="coverStyle">
			<img
				v-if="imageUrl"
				:src="imageUrl"
				alt=""
			/>
			<i v-else class="mbm-iconfont mbm-tupian"></i>
		</div>
		<div class="g-block-image-feature-card__body">
			<div class="g-block-image-feature-card__title-row">
				<div
					class="g-block-image-feature-card__title"
					:style="{ color: normalizedData.titleColor }"
				>{{ normalizedData.title }}</div>
				<i class="mbm-iconfont mbm-youjiantou g-block-image-feature-card__arrow"></i>
			</div>
			<p
				class="g-block-image-feature-card__desc"
				:style="{ color: normalizedData.descColor }"
			>{{ normalizedData.description }}</p>
		</div>

		<ImageGalleryLightbox
			v-model:visible="lightboxVisible"
			:urls="lightboxUrls"
			:initial-index="lightboxIndex"
		/>
	</div>
</template>

<script>
	import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
	import { navigateJumpLink } from '../../../utils/jumpLink.js';
	import {
		getImageFeatureCardBlockStyle,
		getImageFeatureCardImageUrl,
		normalizeImageFeatureCardData,
	} from '../../../utils/imageFeatureCard.js';

	export default {
		name: 'GImageFeatureCardShell',
		components: { ImageGalleryLightbox },
		props: {
			data: {
				type: Object,
				required: true,
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				lightboxVisible: false,
				lightboxUrls: [],
				lightboxIndex: 0,
			};
		},
		computed: {
			normalizedData() {
				return normalizeImageFeatureCardData(this.data);
			},
			blockStyle() {
				return getImageFeatureCardBlockStyle(this.data, { h5: this.h5 });
			},
			cardStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			coverStyle() {
				return {
					aspectRatio: this.blockStyle.coverAspectRatio,
				};
			},
			imageUrl() {
				return getImageFeatureCardImageUrl(this.normalizedData.image);
			},
			isClickable() {
				if (!(this.h5 && !this.editable)) return false;
				return !!this.normalizedData.jumpLink?.type || !!this.imageUrl;
			},
		},
		methods: {
			onWrapClick(e) {
				if (this.editable) {
					e?.stopPropagation?.();
					this.$emit('select');
					return;
				}
				if (!this.isClickable) return;
				if (this.normalizedData.jumpLink?.type) {
					navigateJumpLink(this.normalizedData.jumpLink, {
						apiRequest: this.$apiRequest,
					});
					return;
				}
				if (!this.imageUrl) return;
				this.lightboxUrls = [this.imageUrl];
				this.lightboxIndex = 0;
				this.lightboxVisible = true;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-image-feature-card {
		width: 100%;
		box-sizing: border-box;
		overflow: hidden;
		background: #fff;
		box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);

		&--edit {
			border: 1px solid transparent;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08);
			}
		}

		&.is-clickable {
			cursor: pointer;
		}
	}

	.g-block-image-feature-card__cover {
		width: 100%;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		i {
			font-size: 36px;
			color: #bfbfbf;
		}

		.g-block-image-feature-card--h5 & i {
			font-size: 0.72rem;
		}
	}

	.g-block-image-feature-card__body {
		padding: 16px 18px 18px;
		box-sizing: border-box;

		.g-block-image-feature-card--h5 & {
			padding: 0.32rem 0.36rem 0.36rem;
		}
	}

	.g-block-image-feature-card__title-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;

		.g-block-image-feature-card--h5 & {
			gap: 0.16rem;
		}
	}

	.g-block-image-feature-card__title {
		flex: 1;
		min-width: 0;
		font-size: 17px;
		font-weight: 700;
		line-height: 1.4;
		word-break: break-word;

		.g-block-image-feature-card--h5 & {
			font-size: 0.34rem;
		}
	}

	.g-block-image-feature-card__arrow {
		flex-shrink: 0;
		font-size: 14px;
		color: #c0c0c0;

		.g-block-image-feature-card--h5 & {
			font-size: 0.28rem;
		}
	}

	.g-block-image-feature-card__desc {
		margin: 8px 0 0;
		font-size: 13px;
		line-height: 1.6;
		word-break: break-word;

		.g-block-image-feature-card--h5 & {
			margin-top: 0.16rem;
			font-size: 0.26rem;
		}
	}
</style>
